import { LEAGUE_ACCENTS } from "@/constants/leagueInfo"
import { MAP_PLAYER_RANK_FRAMES_BY_ELEMENT } from "@/constants/mapMarkerAssets"
import {
  ELEMENT_RANK_FRAME_MIN_GUARDIAN_RANK,
  resolveProfileFrame,
} from "@/constants/profileFrames"
import {
  normalizeAvatarVariant,
  resolvePlayerAvatarModule,
} from "@/constants/playerAvatars"
import { AcademicLeague, CardElement } from "@/types/battle"
import { sanitizeProfileFrameForLeague } from "@/utils/profileFrameWarGate"

export type PlayerAvatarSize = "xs" | "sm" | "md" | "lg" | "xl"

const SIZE_PX: Record<PlayerAvatarSize, number> = {
  xs: 32,
  sm: 40,
  md: 56,
  lg: 72,
  xl: 96,
}

function leagueBorderColor(league: AcademicLeague | null | undefined): string {
  if (league === "TITAN") return LEAGUE_ACCENTS.TITAN
  if (league === "ATLANT") return LEAGUE_ACCENTS.ATLANT
  return "rgba(255,255,255,0.2)"
}

export default function PlayerAvatar({
  element,
  avatarVariant = 1,
  nickname = "?",
  academicLeague = null,
  profileFrameKey = null,
  guardianRank = null,
  size = "md",
}: {
  element?: CardElement | null
  avatarVariant?: number | null
  nickname?: string
  academicLeague?: AcademicLeague | null
  profileFrameKey?: string | null
  guardianRank?: number | null
  size?: PlayerAvatarSize
}) {
  const px = SIZE_PX[size]
  const innerSize = Math.round(px * 0.86)
  const effectiveFrameKey = sanitizeProfileFrameForLeague(
    profileFrameKey,
    academicLeague
  )
  const frame = resolveProfileFrame(effectiveFrameKey)
  const borderColor = leagueBorderColor(frame?.league ?? academicLeague)
  const letter = (nickname.trim().charAt(0) || "?").toUpperCase()
  const showElementRankFrame =
    element != null &&
    guardianRank != null &&
    guardianRank >= ELEMENT_RANK_FRAME_MIN_GUARDIAN_RANK
  const rankFrameSize = Math.round(px * 1.22)

  return (
    <div
      className="relative flex shrink-0 items-center justify-center"
      style={{ width: px, height: px }}
    >
      {showElementRankFrame && element ? (
        <img
          src={MAP_PLAYER_RANK_FRAMES_BY_ELEMENT[element]}
          alt=""
          className="pointer-events-none absolute object-contain"
          style={{
            width: rankFrameSize,
            height: rankFrameSize,
            left: (px - rankFrameSize) / 2,
            top: (px - rankFrameSize) / 2,
          }}
        />
      ) : null}
      {element ? (
        <img
          src={resolvePlayerAvatarModule(
            element,
            normalizeAvatarVariant(avatarVariant)
          )}
          alt=""
          className="relative z-[1] rounded-full object-cover"
          style={{
            width: innerSize,
            height: innerSize,
            borderWidth: showElementRankFrame ? 0 : 2,
            borderStyle: "solid",
            borderColor,
          }}
        />
      ) : (
        <div
          className="relative z-[1] flex items-center justify-center rounded-full bg-white/10 font-bold text-white"
          style={{
            width: innerSize,
            height: innerSize,
            borderWidth: 2,
            borderStyle: "solid",
            borderColor,
            fontSize: innerSize * 0.42,
          }}
        >
          {letter}
        </div>
      )}
      {frame && !showElementRankFrame ? (
        <div
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            boxShadow: `0 0 0 2px ${frame.accent}88, 0 0 12px ${frame.accent}44`,
          }}
        />
      ) : null}
    </div>
  )
}
